import { tbtSchedule } from '@bcf-vanilla-ts-v1-shared/misc/tbt-scheduler/tbt-scheduler';
import type { DeviceBaseType } from '@bcf-vanilla-ts-v1-utilities/device-info/types';
import { extractDevice } from '@bcf-vanilla-ts-v1-utilities/device-info/utils';
import type { Observable } from 'rxjs';
import { distinctUntilChanged, fromEvent, map, mapTo, merge, mergeMap, startWith, switchMap, timer } from 'rxjs';
import {
  initWorkerMessage,
  workerAppVisibilityMessage,
  workerBaseHrefMessage,
  workerCookiesMessage,
  workerDeviceInfoMessage,
  workerIsUserActiveInAppMessage,
  workerUrlMessage
} from '../common/messages';
import { provideBrowserSideBlocController } from './browser-side-bloc-controller';
import { registerWorkerInstance } from './tokens';
import type { WorkerWrapper } from './worker-wrapper';

function isUserActiveInApp(): Observable<boolean> {
  return merge(
    merge(fromEvent(document, 'click'), fromEvent(document, 'scroll')).pipe(
      startWith(undefined),
      map(() => true)
    ),
    merge(fromEvent(document, 'click'), fromEvent(document, 'scroll')).pipe(
      startWith(undefined),
      switchMap(() => timer(5 * 60 * 1000)),
      map(() => false)
    )
  ).pipe(distinctUntilChanged());
}

function visibilityChangeEvent(document: Document): string {
  if (typeof document.hidden !== 'undefined') {
    return 'visibilitychange';
  } else if (typeof (document as any).msHidden !== 'undefined') {
    return 'msvisibilitychange';
  } else if (typeof (document as any).webkitHidden !== 'undefined') {
    return 'webkitvisibilitychange';
  }
  return 'visibilitychange';
}

function documentHiddenProp(document: Document): string {
  if (typeof document.hidden !== 'undefined') {
    return 'hidden';
  } else if (typeof (document as any).msHidden !== 'undefined') {
    return 'msHidden';
  } else if (typeof (document as any).webkitHidden !== 'undefined') {
    return 'webkitHidden';
  }
  return 'hidden';
}

function getPageActiveState(document: Document): Observable<boolean> {
  // timer because we dont need this at startup - perf, but timer set to 0 if the page initially is not active
  const documentAny: any = document;
  const delayInMilliseconds: number = documentAny[documentHiddenProp(document)] ? 0 : 5000;
  return timer(delayInMilliseconds).pipe(
    mergeMap(() =>
      merge(
        fromEvent(document, visibilityChangeEvent(document)).pipe(
          map(() => !documentAny[documentHiddenProp(document)])
        ),
        fromEvent(document, 'pause').pipe(mapTo(false)),
        fromEvent(document, 'resume').pipe(mapTo(true))
      )
    ),
    startWith(!documentAny[documentHiddenProp(document)])
  );
}

function getBaseHref(document: Document): string | undefined {
  const href: string | undefined = document.querySelector('base')?.getAttribute('href') ?? undefined;
  if (href) {
    return new URL(href, document.baseURI).pathname;
  }
  return undefined;
}

export function updateWorkerToolkitFromUi(
  worker: Worker | WorkerWrapper,
  document: Document,
  deviceKindBase: DeviceBaseType
): void {
  let previousCookies: string | undefined;
  let previousPathname: string | undefined;
  // fetching cookies is heavy on main thread
  const setupCookies = () => {
    const currentCookies: string = document.cookie;
    if (previousCookies !== currentCookies) {
      worker.postMessage(workerCookiesMessage(document.cookie));
      previousCookies = currentCookies;
    }
  };

  const setupWorkerUrls = () => {
    const currentPathname: string = document.location.pathname;
    if (previousPathname !== currentPathname) {
      worker.postMessage(workerUrlMessage(currentPathname, document.location.href));
      previousPathname = document.location.pathname;
    }
  };

  tbtSchedule(() => {
    setupCookies();
    setupWorkerUrls();
    if (globalThis.cookieStore) {
      cookieStore.addEventListener('change', () => setupCookies());
    } else {
      setInterval(() => {
        setupCookies();
      }, 100);
    }
    setInterval(() => {
      setupWorkerUrls();
    }, 100);
  });
  worker.postMessage(workerBaseHrefMessage(getBaseHref(document) ?? ''));
  worker.postMessage(workerDeviceInfoMessage(extractDevice(deviceKindBase, window, navigator)));
  isUserActiveInApp().subscribe((isUserActiveInApp: boolean) =>
    worker.postMessage(workerIsUserActiveInAppMessage(isUserActiveInApp))
  );

  getPageActiveState(document).subscribe((state: boolean) => worker.postMessage(workerAppVisibilityMessage(state)));
}

export function provideWorker(worker: Worker, appRuntimeId: number, deviceKindBase: DeviceBaseType): void {
  worker.postMessage(initWorkerMessage(appRuntimeId, document.location.hostname));
  updateWorkerToolkitFromUi(worker, document, deviceKindBase);
  registerWorkerInstance(worker);
  provideBrowserSideBlocController().init();
}
